import {
  faCheck,
  faEnvelope,
  faPen,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import AnchorButton from "../../components/AnchorButton";
import Button from "../../components/Button";
import Container from "../../components/Container";
import Divider from "../../components/Divider";
import Icon from "../../components/Icon";
import SelectInput from "../../components/SelectInput";
import Spacer from "../../components/Spacer";
import TextField from "../../components/TextField";
import sendCodeToEmail from "../../helpers/sendCodeToEmail";
import useTickets, { TicketProps } from "../../hooks/useTickets";

const TicketList = () => {
  const tickets = useTickets();
  const [sentList, setSentList] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(perPage);
  const [pages, setPages] = useState(1);
  const [filteredTickets, setFilteredTickets] = useState<TicketProps[]>([]);
  const results = (filteredTickets.length ? filteredTickets : tickets).slice(
    start,
    limit
  );

  // Calculate start and limit
  useEffect(() => {
    setStart(perPage * (page - 1));
    setLimit(perPage * (page - 1) + perPage);
  }, [perPage, page]);

  // Calculate number of pages
  useEffect(() => {
    if (search) {
      setPages(Math.ceil(results.length / perPage));
    } else {
      setPages(Math.ceil(tickets.length / perPage));
    }
  }, [tickets, perPage, search, results]);

  // Search functionality
  useEffect(() => {
    if (search) {
      const filteredTickets = tickets.filter((ticket) =>
        ticket.email.includes(search)
      );
      console.log(filteredTickets);
      setFilteredTickets(filteredTickets);
    } else {
      setFilteredTickets([]);
    }
  }, [search, tickets]);

  return (
    <div className="TicketListPage">
      <Container>
        <Spacer>
          <div className="my-4 text-lg font-medium text-aqua">Admin List</div>
          <div className="my-4 text-5xl font-bold leading-none">
            Ticket List
          </div>
          <Divider />
        </Spacer>

        <div className="flex flex-wrap w-full lg:space-x-4 lg:flex-no-wrap">
          <div className="w-8/12 space-y-2">
            <div className="text-xs text-gray-300">Search Email</div>
            <TextField
              type="email"
              placeholder="Search Email Address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-4/12 space-y-2">
            <div className="text-xs text-gray-300">Per Page</div>
            <SelectInput
              value={perPage}
              onChange={(e) => setPerPage(parseInt(e.target.value))}
              options={[
                { text: "10", value: 10 },
                { text: "20", value: 20 },
                { text: "30", value: 30 },
                { text: "40", value: 40 },
                { text: "50", value: 50 },
                { text: "60", value: 60 },
                { text: "70", value: 70 },
                { text: "80", value: 80 },
                { text: "90", value: 90 },
                { text: "100", value: 100 },
              ]}
            />
          </div>
          <div className="w-2/12 mt-4 space-y-2 lg:mt-0">
            <div className="text-xs text-gray-300">Add Ticket</div>
            <AnchorButton href="/admin/tickets/add" theme="secondary">
              <Icon icon={faPlus} /> Add Ticket
            </AnchorButton>
          </div>
        </div>

        <Spacer>
          <div className="space-y-8">
            {results.length ? (
              results.map((ticket, key) => {
                return (
                  <div
                    className="w-full p-4 space-y-4 bg-gray-100 rounded"
                    key={key}
                  >
                    <div className="flex flex-wrap w-full space-y-4 lg:space-y-0 lg:flex-no-wrap">
                      <div className="w-full lg:w-8/12">
                        <div className="text-xs text-gray-300">Email</div>
                        <div className="text-xl text-cream">{ticket.email}</div>
                      </div>
                      <div className="w-full lg:w-4/12">
                        <div className="text-xs text-gray-300">Ticket Code</div>
                        <div className="text-xl text-aqua">{ticket.code}</div>
                      </div>
                    </div>

                    <Divider />

                    <div className="flex flex-wrap w-full space-y-4 lg:space-y-0 lg:flex-no-wrap">
                      {ticket.firstname ? (
                        <div className="w-full lg:w-4/12">
                          <div className="text-xs text-gray-300">
                            First name
                          </div>
                          <div className="text-sm text-gray-400">
                            {ticket.firstname}
                          </div>
                        </div>
                      ) : null}
                      {ticket.lastname ? (
                        <div className="w-full lg:w-4/12">
                          <div className="text-xs text-gray-300">Last Name</div>
                          <div className="text-sm text-gray-400">
                            {ticket.lastname}
                          </div>
                        </div>
                      ) : null}
                      {ticket.code2 ? (
                        <div className="w-full lg:w-4/12">
                          <div className="text-xs text-gray-300">
                            Alternative Ticket Code
                          </div>
                          <div className="text-sm text-gray-400">
                            {ticket.code2}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap w-full space-y-4 lg:space-y-0 lg:flex-no-wrap">
                      <div className="w-full lg:w-4/12">
                        <div className="text-xs text-gray-300">Ticket Type</div>
                        <div className="text-sm text-gray-400">
                          {ticket.type}
                        </div>
                      </div>
                      <div className="w-full lg:w-4/12">
                        <div className="text-xs text-gray-300">
                          Ticket Order
                        </div>
                        <div className="text-sm text-gray-400">
                          {ticket.order}
                        </div>
                      </div>
                      <div className="w-full lg:w-4/12">
                        <div className="text-xs text-gray-300">Status</div>
                        <div className="text-sm text-gray-400">
                          <span className="text-sm">
                            {ticket.status === "active" ? (
                              <Icon icon={faCheck} />
                            ) : (
                              <Icon icon={faTimes} />
                            )}
                          </span>{" "}
                          {ticket.status}
                        </div>
                      </div>
                    </div>
                    <div className="w-full space-y-4 lg:space-y-0 lg:space-x-4">
                      <AnchorButton href={`/admin/tickets/${ticket.email}`}>
                        <Icon icon={faPen} /> Edit Ticket
                      </AnchorButton>
                      <Button
                        theme="outline"
                        onClick={() => {
                          sendCodeToEmail(ticket.email, ticket.code);
                          setSentList((prev) => [...prev, ticket.email]);
                        }}
                      >
                        {sentList.includes(ticket.email) ? (
                          <>
                            <Icon icon={faCheck} /> Sent!
                          </>
                        ) : (
                          <>
                            <Icon icon={faEnvelope} /> Send Ticket to User
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="w-full p-4 my-2 text-sm text-center text-gray-400 bg-gray-100 rounded">
                No tickets.
              </div>
            )}
          </div>
        </Spacer>

        <Spacer>
          <ul className="flex overflow-hidden overflow-x-scroll rounded">
            {[...Array(pages)].map((e, key) => (
              <li key={key}>
                <button
                  type="button"
                  className={`px-4 py-2 transition-colors duration-300 ease-in  ${
                    key + 1 === page
                      ? "bg-aqua text-white font-bold"
                      : "text-gray-400 bg-gray-100 hover:bg-gray-200"
                  }`}
                  onClick={() => {
                    setPage(key + 1);
                  }}
                >
                  {key + 1}
                </button>
              </li>
            ))}
          </ul>
        </Spacer>
      </Container>
    </div>
  );
};

export default TicketList;
